// src/components/sdk/SdkRegisterContent.tsx
import React from 'react';
import {
    Form,
    Input,
    Button,
    Card,
    Typography,
    Alert,
    Space,
    message,
} from 'antd';
import {
    AppstoreOutlined,
    UserOutlined,
    LockOutlined,
    CheckCircleOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import { sdkMgmt } from '../../server/SdkMgmt';
import { RegisterFormData, RegisterResponse, SdkRegisterContentProps } from './SdkInterface';

const { Title, Text } = Typography;

interface SdkRegisterContentState {
    loading: boolean;
    errorMessage: string;
}

class SdkRegisterContent extends React.Component<SdkRegisterContentProps, SdkRegisterContentState> {
    formRef = React.createRef<FormInstance>();

    constructor(props: SdkRegisterContentProps) {
        super(props);
        this.state = {
            loading: false,
            errorMessage: '',
        };
    }

    // 验证用户名格式
    validateUsername = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('请输入用户名'));
        }
        if (value.length < 2) {
            return Promise.reject(new Error('用户名长度不能少于2位'));
        }
        if (value.length > 20) {
            return Promise.reject(new Error('用户名长度不能超过20位'));
        }
        // 只允许字母、数字、下划线
        const usernameRegex = /^[a-zA-Z0-9_]+$/;
        if (!usernameRegex.test(value)) {
            return Promise.reject(new Error('用户名只能包含字母、数字和下划线'));
        }
        return Promise.resolve();
    };

    // 验证密码强度
    validatePassword = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('请设置登录密码'));
        }
        if (value.length < 8) {
            return Promise.reject(new Error('密码长度不能少于8位'));
        }
        if (!/[A-Z]/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个大写字母'));
        }
        if (!/[a-z]/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个小写字母'));
        }
        if (!/\d/.test(value)) {
            return Promise.reject(new Error('密码需包含至少一个数字'));
        }
        return Promise.resolve();
    };

    // 验证确认密码
    validateConfirmPassword = (_: any, value: string) => {
        const password = this.formRef.current?.getFieldValue('password');
        if (!value) {
            return Promise.reject(new Error('请确认密码'));
        }
        if (value !== password) {
            return Promise.reject(new Error('两次输入的密码不一致'));
        }
        return Promise.resolve();
    };

    // 提交注册
    handleSubmit = async (values: RegisterFormData) => {
        const DEBUG = false;
        const TAG = `RegisterView.handleSubmit() - `;
        this.setState({ loading: true, errorMessage: '' });

        try {
            const { email, password } = values;

            if (DEBUG) {
                console.log(TAG, `username:`, email);
                console.log(TAG, `password:`, password);
            }

            // 如果使用真实 SDK，应该是：
            let response = await sdkMgmt.register(email, password);
            const result: RegisterResponse = response;
            DEBUG && console.log(TAG, `result:\n`, result);

            if (response.success) {
                message.success({
                    content: '注册成功!',
                    icon: <CheckCircleOutlined />,
                    duration: 3,
                });

                this.props.onRegisterSuccess?.({
                    email: values.email,
                });
                this.formRef.current?.resetFields();
            } else {
                const errorMsg = response.message || '注册失败，请稍后重试';
                this.setState({ errorMessage: errorMsg });
                message.error(errorMsg);
                this.props.onRegisterFail?.(response);
            }
        } catch (error: any) {
            const errorMsg = error.message || '网络错误，请稍后重试';
            this.setState({ errorMessage: errorMsg });
            message.error(errorMsg);
            this.props.onRegisterFail?.(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { trans, onBack } = this.props;
        const { loading, errorMessage } = this.state;

        return (
            <Card
                bordered={false}
                style={{
                    borderRadius: 16,
                    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                    padding: '8px 4px',
                }}
            >
                {/* 返回按钮 */}
                {/* <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={onBack}
                    style={{ marginBottom: 16, paddingLeft: 0 }}
                >
                    返回登录
                </Button> */}

                {/* Logo 区域 */}
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 12,
                        }}
                    >
                        <AppstoreOutlined style={{ fontSize: 24, color: '#fff' }} />
                    </div>
                    <Title level={3} style={{ marginBottom: 0 }}>
                        注册开发者账号
                    </Title>
                    <Text type="secondary">
                        创建您的 SDK 应用
                    </Text>
                </div>

                {errorMessage && (
                    <Alert
                        message={errorMessage}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: 24 }}
                        onClose={() => this.setState({ errorMessage: '' })}
                    />
                )}

                <Form
                    ref={this.formRef}
                    name="register"
                    onFinish={this.handleSubmit}
                    layout="vertical"
                    size="large"
                    requiredMark={false}
                >
                    <Form.Item
                        name="email"
                        label="用户名"
                        rules={[
                            { required: true, message: '请输入用户名' },
                            { validator: this.validateUsername },
                        ]}
                        extra="2-20位，只能包含字母、数字和下划线"
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="请设置用户名"
                            autoComplete="username"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="登录密码"
                        rules={[
                            { required: true, message: '请设置登录密码' },
                            { validator: this.validatePassword },
                        ]}
                        extra="至少8位，需包含大小写字母和数字"
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="请设置登录密码"
                            autoComplete="new-password"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="确认密码"
                        rules={[
                            { required: true, message: '请确认密码' },
                            { validator: this.validateConfirmPassword },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="请再次输入密码"
                            autoComplete="new-password"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={loading}
                            size="large"
                            style={{
                                height: 44,
                                borderRadius: 8,
                                fontSize: 16,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                            }}
                        >
                            {loading ? '注册中...' : '注 册'}
                        </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center' }}>
                        <Space>
                            <Text type="secondary">已有账号？</Text>
                            <Button
                                type="link"
                                style={{ padding: 0, color: '#667eea' }}
                                onClick={onBack}
                            >
                                立即登录
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Card>
        );
    }
}

export default SdkRegisterContent;